import React, {useRef, useEffect} from "react";
import "./ContentEditable.css";
import "./FieldColor.css";
import cn from "classnames";
import {useSetting} from "../../contexts/setting";
import {useTextState} from "../../hooks/useTextState";
import {useFieldColor} from "../../hooks/useFieldColor";
import {toggleModulFont, normalizeContentEditableText} from "../../libs/contentEditableFont";
import {contentEditableHtml, contentEditableListHtml, isRichEditableText, storeContentEditableHtml} from "../../libs/contentEditableText";
import {PLACEHOLDERS} from "../../data/constants";
import {applyTypograf, revertTypograf, ensureProductNameNbsp} from "../../libs/typograf";
import {
  insertContentEditableLineBreak,
  mergeContentEditableHighlightLines,
  normalizeContentEditableHtml,
} from "../../libs/contentEditableLines";
import _CE from "react-contenteditable";
// Bun's bundler doesn't unwrap CJS `exports.default` for this package —
// `import X from "pkg"` yields `{default: Component}` instead of Component.
const CE = _CE.default || _CE;

/** Check if list-formatted text matches a placeholder for the given type. */
function isPlaceholderList(htmlText, type) {
  if (!type || !htmlText) return false;
  for (const lang in PLACEHOLDERS) {
    const placeholder = PLACEHOLDERS[lang][type];
    if (!placeholder) continue;
    // Format the placeholder as a list and compare
    const asList = placeholder
      .split("\n")
      .map((line) => `<li>${line.replace(/^[•\-*]\s*/, "")}</li>`)
      .join("");
    if (asList === htmlText) return true;
  }
  return false;
}

const ContentEditable = ({
  text = "",
  id,
  colorId = id,
  inline = false,
  plain = false,
  normalize = undefined,
  variation = "default",
  color = "#F1EC19",
  isList = false,
  enableBoldHotkey = false,
  enableBoldAccentHotkey = false,
  enableWhiteHighlightHotkey = true,
  boldAccentColor = "#FCF84A",
  highlightStyle = "background",
  onAccentColorUsageChange = undefined,
  placeholder = undefined,
  style = {},
}) => {
  if (highlightStyle !== "background" && highlightStyle !== "text-color") {
    throw new Error(`Unsupported ContentEditable highlightStyle: ${highlightStyle}`);
  }
  const {fontOption, isUppercase, layoutOption, isTypografEnabled} = useSetting();
  const contentEditableRef = useRef(null);
  const labelRef = useRef(null);

  const {
    type,
    currentText,
    onTextChange,
    fontSizeClass,
    isPlaceholder: isPlaceholderBase,
    isLocked,
  } = useTextState(id, text, labelRef);
  const {color: fieldColor, cascade} = useFieldColor(type, colorId);

  // Helper to check Mac/Win
  const isHotkey = (e) => (e.ctrlKey || e.metaKey) && e.shiftKey;
  const isBoldHotkeyFn = (e) =>
    enableBoldHotkey &&
    (e.ctrlKey || e.metaKey) &&
    !e.shiftKey &&
    !e.altKey &&
    (e.code === "KeyB" || e.key?.toLowerCase() === "b");
  const isBoldAccentHotkeyFn = (e) =>
    enableBoldAccentHotkey &&
    (e.ctrlKey || e.metaKey) &&
    ((e.shiftKey && !e.altKey) || (e.altKey && !e.shiftKey)) &&
    (e.code === "KeyB" || e.key?.toLowerCase() === "b");

  const htmlText = contentEditableHtml(currentText, plain);
  const displayText = normalizeContentEditableHtml(isList ? contentEditableListHtml(htmlText) : htmlText, cascade);
  const onHtmlChange = (html) => {
    onTextChange(storeContentEditableHtml(html, plain || isRichEditableText(currentText)));
  };

  // For lists, also check if the list-formatted placeholder matches
  const isPlaceholder = isPlaceholderBase || (isList && isPlaceholderList(displayText, type));

  useEffect(() => {
    if (!onAccentColorUsageChange || highlightStyle !== "text-color") return;
    const usesAccentColor = Boolean(
      contentEditableRef.current?.querySelector("span.highlight.text-color.color")
    );
    onAccentColorUsageChange(usesAccentColor);
  }, [displayText, highlightStyle, onAccentColorUsageChange]);

  const textareaClass = cn("textarea", `font-${fontOption}`, fontSizeClass, {
    uppercase: isUppercase,
    "is-list": isList,
  });

  const finalTextareaClass = cn(textareaClass, "content-editable", {
    "placeholder-text": isPlaceholder,
    "plain-text-layout": plain,
  });

  const getSelectionObj = () => {
    const sel = window.getSelection();
    if (
      sel.rangeCount > 0 &&
      contentEditableRef.current &&
      contentEditableRef.current.contains(sel.anchorNode)
    ) {
      return sel;
    }
    return null;
  };

  const wrapSelectionWithSpan = (sel, classNames) => {
    const range = sel.getRangeAt(0);
    if (!range.collapsed) {
      const span = document.createElement("span");
      classNames.forEach((cn) => span.classList.add(cn));
      span.textContent = range.toString();
      range.deleteContents();
      range.insertNode(span);
      sel.removeAllRanges();
      const newRange = document.createRange();
      newRange.setStartAfter(span);
      newRange.setEndAfter(span);
      sel.addRange(newRange);
    }
  };

  const getSelectionClassWrappers = (sel, className) => {
    if (!contentEditableRef.current || !sel?.rangeCount) return [];
    const range = sel.getRangeAt(0);
    const wrappers = Array.from(contentEditableRef.current.querySelectorAll(`span.${className}`));
    return wrappers.filter((wrapper) => {
      try {
        return range.intersectsNode(wrapper);
      } catch (_err) {
        return false;
      }
    });
  };

  const unwrapSelectionClass = (sel, className) => {
    const wrappers = getSelectionClassWrappers(sel, className);
    wrappers.forEach((wrapper) => {
      wrapper.classList.remove(className);
      if (Array.from(wrapper.classList).length > 0) return;
      while (wrapper.firstChild) {
        wrapper.parentNode.insertBefore(wrapper.firstChild, wrapper);
      }
      wrapper.remove();
    });
    contentEditableRef.current?.normalize();
  };

  const removeAllSpans = (root) => {
    const spans = root.querySelectorAll("span");
    spans.forEach((span) => {
      const txt = document.createTextNode(span.textContent);
      span.parentNode.replaceChild(txt, span);
    });
  };

  const commitFormattedValue = () => {
    if (!contentEditableRef.current) return;
    normalizeContentEditableText(contentEditableRef.current, normalize);
    onHtmlChange(contentEditableRef.current.innerHTML);
  };

  const handleFormatting = (action, sel) => {
    switch (action) {
      case "clear":
        removeAllSpans(contentEditableRef.current);
        commitFormattedValue();
        break;
      case "white":
        if (sel && !sel.getRangeAt(0).collapsed) {
          wrapSelectionWithSpan(
            sel,
            highlightStyle === "text-color" ? ["highlight", "text-color"] : ["highlight"]
          );
          commitFormattedValue();
        }
        break;
      case "color":
        if (sel && !sel.getRangeAt(0).collapsed) {
          wrapSelectionWithSpan(
            sel,
            highlightStyle === "text-color"
              ? ["highlight", "text-color", "color"]
              : ["highlight", "color"]
          );
          commitFormattedValue();
        }
        break;
      default:
        break;
    }
  };

  const onKeyDown = (e) => {
    if (inline && e.key === "Enter") {
      e.preventDefault();
      return;
    }
    if (isList && e.key === "Enter") return;

    if (
      (e.metaKey || e.ctrlKey) && e.altKey && !e.shiftKey &&
      (e.code === "KeyA" || e.key?.toLowerCase() === "a")
    ) {
      e.preventDefault();
      const selection = getSelectionObj();
      if (selection && toggleModulFont(contentEditableRef.current, selection)) {
        commitFormattedValue();
      }
      return;
    }

    if (isBoldAccentHotkeyFn(e)) {
      e.preventDefault();
      const sel = getSelectionObj();
      if (sel && !sel.getRangeAt(0).collapsed) {
        if (getSelectionClassWrappers(sel, "bold-accent").length > 0) {
          document.execCommand("removeFormat", false, null);
          unwrapSelectionClass(sel, "bold-accent");
        } else {
          wrapSelectionWithSpan(sel, ["bold-accent"]);
        }
        commitFormattedValue();
      }
      return;
    }

    if (isBoldHotkeyFn(e)) {
      e.preventDefault();
      const sel = getSelectionObj();
      if (sel && !sel.getRangeAt(0).collapsed) {
        document.execCommand("bold", false, null);
        commitFormattedValue();
      }
      return;
    }

    if (type === "features" && e.key === "Enter" && !isList) {
      e.preventDefault();
      const selection = window.getSelection();
      if (!selection.rangeCount) return;
      const range = selection.getRangeAt(0);
      range.deleteContents();
      const textNode = document.createTextNode("\n• ");
      range.insertNode(textNode);
      range.setStartAfter(textNode);
      range.setEndAfter(textNode);
      selection.removeAllRanges();
      selection.addRange(range);
      onTextChange(contentEditableRef.current.innerText);
      return;
    }

    if (e.key === "Enter" && !isList) {
      e.preventDefault();
      const selection = getSelectionObj();
      if (selection && insertContentEditableLineBreak(contentEditableRef.current, selection, plain)) {
        commitFormattedValue();
      }
      return;
    }

    if ((e.key === "Backspace" || e.key === "Delete") && !isList) {
      const selection = getSelectionObj();
      const direction = e.key === "Backspace" ? "backward" : "forward";
      if (
        selection &&
        mergeContentEditableHighlightLines(contentEditableRef.current, selection, direction)
      ) {
        e.preventDefault();
        commitFormattedValue();
        return;
      }
    }

    if (isHotkey(e)) {
      const action =
        e.code === "Digit1" || e.key === "!"
          ? enableWhiteHighlightHotkey
            ? "white"
            : null
          : e.code === "Digit2" || e.key === "@"
            ? "color"
            : e.code === "Digit0" || e.key === ")"
              ? "clear"
              : null;
      if (!action) return;
      e.preventDefault();
      const sel = getSelectionObj();
      if (action === "clear") {
        handleFormatting("clear");
        return;
      }
      if (sel && !sel.getRangeAt(0).collapsed) {
        handleFormatting(action, sel);
      }
    }
  };

  const onPaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, pastedText);
  };

  // Normalize text nodes without changing formatting tags.
  const handleCEChange = () => {
    normalizeContentEditableText(contentEditableRef.current, normalize);
    onHtmlChange(contentEditableRef.current.innerHTML);
  };

  // Apply typograf normalization on blur. Typograf understands HTML and
  // leaves tags untouched, so rich-formatted content (<span class="highlight">…)
  // is preserved. Pulls from the live DOM (no currentText fallback) — if the
  // ref is null, plain-text currentText would be set as innerHTML and wipe
  // every formatting span, so we'd rather skip entirely.
  //
  // react-contenteditable's shouldComponentUpdate normalizes NBSPs (and their
  // narrow/thin variants) to regular spaces before diffing new html against
  // live innerHTML — meaning a state update whose ONLY change is NBSP
  // insertion never reaches the DOM. Write the typografed HTML directly on
  // the ref so the DOM catches up, then commit to state for persistence.
  const handleCEBlur = () => {
    if (!contentEditableRef.current || isPlaceholder || isLocked) return;
    const source = contentEditableRef.current.innerHTML;
    let normalized = source;
    if (isTypografEnabled) {
      normalized = applyTypograf(source, fontOption);
    } else {
      // Product name nbsp insertion still applies even without typograf
      normalized = ensureProductNameNbsp(source);
    }
    if (normalized !== source) {
      contentEditableRef.current.innerHTML = normalized;
      onHtmlChange(normalized);
    }
  };

  // Instant re-normalization on Typograf toggle transitions: apply on OFF→ON,
  // strip typograf artifacts on ON→OFF. Same DOM-first rule as handleCEBlur —
  // the ref is the only safe HTML source; currentText may be plain text.
  const wasTypografEnabledRef = useRef(isTypografEnabled);
  useEffect(() => {
    const wasEnabled = wasTypografEnabledRef.current;
    wasTypografEnabledRef.current = isTypografEnabled;
    if (
      wasEnabled === isTypografEnabled ||
      isPlaceholder ||
      isLocked ||
      !contentEditableRef.current
    )
      return;
    const source = contentEditableRef.current.innerHTML;
    const next = isTypografEnabled ? applyTypograf(source, fontOption) : revertTypograf(source);
    if (next !== source) {
      // Same NBSP-normalization caveat as handleCEBlur — write DOM directly.
      contentEditableRef.current.innerHTML = next;
      onHtmlChange(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: trigger only on toggle transition
  }, [isTypografEnabled]);

  // Toggle product-name-empty class on parent .product-name to hide/show separator
  useEffect(() => {
    if (type !== "product-name") return;
    const parent = labelRef.current?.closest(".product-name");
    if (!parent) return;
    const plainText = currentText.replace(/<[^>]*>/g, "").trim();
    if (!plainText) {
      parent.classList.add("product-name-empty");
    } else {
      parent.classList.remove("product-name-empty");
    }
  });

  const labelClass = cn("input-sizer", fontSizeClass);
  const ceClass = cn("textarea-wrap ce-wrap", variation);
  // react-contenteditable skips renders when HTML is unchanged; event handlers must stay current.
  const onKeyDownRef = useRef(onKeyDown);
  onKeyDownRef.current = onKeyDown;
  const onBlurRef = useRef(handleCEBlur);
  onBlurRef.current = handleCEBlur;

  return (
    <div
      className={ceClass}
      ref={labelRef}
      data-field-id={id}
      style={{"--bg-color": color, "--bold-accent-color": boldAccentColor}}
    >
      <CE
        style={fieldColor ? {...style, "--coverbaker-field-color": fieldColor} : style}
        data-field-color={fieldColor}
        tagName={isList ? "ul" : "div"}
        innerRef={contentEditableRef}
        className={finalTextareaClass}
        data-placeholder={placeholder}
        aria-label={placeholder}
        html={displayText}
        disabled={false}
        onChange={handleCEChange}
        onBlur={(event) => onBlurRef.current(event)}
        onKeyDown={(event) => onKeyDownRef.current(event)}
        onPaste={onPaste}
      />
    </div>
  );
};

export default ContentEditable;
